import React, { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import ReactLoading from "react-loading";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  API_BASE,
  NODE_TYPES,
  DEFAULT_PROPS,
  PRESETS,
  COMP_PRESETS,
  BIORECIPE_COLS,
  buildColorMap,
  buildBioRECIPERows,
  generateCombinations,
} from "./motifUtils";
import { MotifPreview } from "./MotifComponents";

const SPONT_BALANC_OPTIONS = ["none", "0", "decrease,0", "increase,0"];

// Every preset runs independently — no groups
const PRESET_GROUPS = {};

// Run one locked preset through the API, carrying over spontaneous/balancing from currentNodeProps
async function runOneModel(presetKey, currentNodeProps, currentNodes) {
  const p = PRESETS[presetKey] || COMP_PRESETS[presetKey];
  if (!p) throw new Error(`Preset ${presetKey} not found`);

  // Build nodeProps for this preset, merging spontaneous/balancing from the loaded model
  const np = {};
  p.nodes.forEach((n) => {
    const fixed = p.nodeDefaults?.[n.name] || {};
    const matching = currentNodes.find((cn) => cn.name === n.name);
    const carried = matching ? currentNodeProps[matching.id] : null;
    np[n.id] = {
      ...DEFAULT_PROPS[n.type],
      ...fixed,
      spontaneous: carried?.spontaneous || "none",
      balancing: carried?.balancing || "none",
    };
  });

  const rows = buildBioRECIPERows(
    p.nodes,
    p.edges,
    np,
    null,
    null,
    p.regulationRules,
  );

  const firstRow = rows[0] || {};
  const slCount = Object.keys(firstRow).filter((k) =>
    k.startsWith("State List"),
  ).length;
  const slCols = Array.from({ length: slCount }, (_, i) => `State List ${i}`);
  const dynCols = BIORECIPE_COLS.filter(
    (c) => !c.startsWith("State List"),
  ).reduce(
    (acc, c) => (c === "Levels" ? [...acc, c, ...slCols] : [...acc, c]),
    [],
  );

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(rows, { header: dynCols }),
    "Sheet1",
  );
  const blob = new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fname = `${presetKey}.xlsx`;

  const uf = new FormData();
  uf.append("file", blob, fname);
  const ur = await fetch(`${API_BASE}/load-model`, {
    method: "POST",
    body: uf,
  });
  if (!ur.ok) {
    const t = await ur.text();
    throw new Error(`Upload failed for ${presetKey}: ${t.slice(0, 200)}`);
  }

  const scenarioList = Array.from({ length: p.scenarios }, (_, i) =>
    String(i + 1),
  );
  const runParams = {
    filename: fname,
    steps: 50,
    runs: 10,
    scenarios: scenarioList,
    scheme: "random",
    output_format: 1,
    normalize: false,
    difference: false,
    concatenate: false,
    randomize_each_run: false,
    round_trace: false,
    timed: false,
  };
  console.log(
    `[MOTIF SIM] run-script params for ${presetKey}:`,
    JSON.stringify(runParams, null, 2),
  );
  const rr = await fetch(`${API_BASE}/run-script`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(runParams),
  });
  const txt = await rr.text();
  let d;
  try {
    d = JSON.parse(txt);
  } catch {
    throw new Error(`Invalid response for ${presetKey}`);
  }
  if (!rr.ok || !d?.success)
    throw new Error(d?.error || `Simulation failed for ${presetKey}`);
  const rawData = d.traces_data?.data || {};
  console.log(`[MOTIF SIM] ${presetKey} scenario keys:`, Object.keys(rawData));
  return {
    key: presetKey,
    label: p.label,
    data: rawData,
    traceFiles: d.trace_files || [],
  };
}

// ─────────────────────────────────────────────
const MotifSimulate = ({
  nodes,
  edges,
  nodeProps,
  setNodeProps,
  loadedPresetKey,
  onLoadPreset,
  bioText,
  bioLoading,
  setBioText,
  setBioLoading,
  presetLibrary = "biological",
}) => {
  const preset =
    PRESETS[loadedPresetKey] || COMP_PRESETS[loadedPresetKey] || null;
  const isLocked = !!preset?.locked;
  const groupKeys = loadedPresetKey
    ? PRESET_GROUPS[loadedPresetKey] || null
    : null;
  const isGroup = !!groupKeys;

  const [showParams, setShowParams] = useState(false);
  const [simSteps, setSimSteps] = useState(50);
  const [simRuns, setSimRuns] = useState(10);
  const [simScenarios, setSimScenarios] = useState("1");
  const [simScheme, setSimScheme] = useState("sync");
  const [simLevels, setSimLevels] = useState(2);
  const [useAutoCombos, setUseAutoCombos] = useState(false);

  // Accumulated results: { [runKey]: { label, color, data, traceFiles } }
  const [allResults, setAllResults] = useState({});
  const [simRunning, setSimRunning] = useState(false);
  const [simError, setSimError] = useState(null);
  const [activeModel, setActiveModel] = useState(null);
  const [pickedScenario, setPickedScenario] = useState([]);

  // Color palette for model comparison
  const MODEL_COLORS = [
    "#003594",
    "#036b45",
    "#770538",
    "#B85C00",
    "#5B21B6",
    "#0369A1",
    "#B45309",
    "#065F46",
  ];
  const getModelColor = (idx) => MODEL_COLORS[idx % MODEL_COLORS.length];

  const currentRunKey = loadedPresetKey || null;
  const res = allResults[currentRunKey];

  // Build a signature of current params to detect changes
  const currentParamSig = JSON.stringify({
    spontaneous: nodes.map(
      (n) => (nodeProps[n.id] || {}).spontaneous || "none",
    ),
    balancing: nodes.map((n) => (nodeProps[n.id] || {}).balancing || "none"),
  });
  const alreadyRan = currentRunKey && res && res.paramSig === currentParamSig;

  // Preset preview
  const [previewKey, setPreviewKey] = useState(loadedPresetKey || null);
  const [subActive, setSubActive] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  // ── Run simulation ──
  const runSimulation = useCallback(async () => {
    if (nodes.length === 0 || alreadyRan) return;
    setSimRunning(true);
    setSimError(null);
    const runKey = currentRunKey;
    const modelColor = getModelColor(Object.keys(allResults).length);
    try {
      if (isGroup) {
        // Run all models in the group sequentially
        const groupResults = {};
        for (const key of groupKeys) {
          const res = await runOneModel(key, nodeProps, nodes);
          groupResults[key] = {
            label: res.label,
            data: res.data,
            traceFiles: res.traceFiles,
          };
        }
        setAllResults((prev) => ({
          ...prev,
          [runKey]: {
            label: preset?.label || runKey,
            color: modelColor,
            type: "group",
            models: groupResults,
            paramSig: currentParamSig,
          },
        }));
        const firstKey = groupKeys[0];
        const firstData = groupResults[firstKey]?.data || {};
        setActiveModel(firstKey);
        setPickedScenario(Object.keys(firstData));
      } else {
        // Single preset or free model
        const scenarioCount = isLocked
          ? preset.scenarios
          : useAutoCombos
            ? Number(simScenarios) || 4
            : null;
        const levelsToUse = isLocked
          ? preset.levels
          : useAutoCombos
            ? simLevels
            : null;
        const rows = buildBioRECIPERows(
          nodes,
          edges,
          nodeProps,
          scenarioCount,
          levelsToUse,
          preset?.regulationRules,
        );
        // Debug: log state lists being written
        console.log(
          "[MOTIF SIM] State lists in Excel:",
          rows.map((r) => ({
            name: r["Element Name"],
            sl: Object.keys(r)
              .filter((k) => k.startsWith("State List"))
              .map((k) => `${k}:${r[k]}`)
              .join(", "),
          })),
        );

        const firstRow = rows[0] || {};
        const slCount = Object.keys(firstRow).filter((k) =>
          k.startsWith("State List"),
        ).length;
        const slCols = Array.from(
          { length: slCount },
          (_, i) => `State List ${i}`,
        );
        const dynCols = BIORECIPE_COLS.filter(
          (c) => !c.startsWith("State List"),
        ).reduce(
          (acc, c) => (c === "Levels" ? [...acc, c, ...slCols] : [...acc, c]),
          [],
        );

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(rows, { header: dynCols }),
          "Sheet1",
        );
        const blob = new Blob(
          [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
          {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        );

        const uf = new FormData();
        uf.append("file", blob, "motif_model.xlsx");
        const ur = await fetch(`${API_BASE}/load-model`, {
          method: "POST",
          body: uf,
        });
        if (!ur.ok) {
          const t = await ur.text();
          throw new Error(`Upload failed (${ur.status}): ${t.slice(0, 200)}`);
        }

        const scenarioList = isLocked
          ? Array.from({ length: preset.scenarios }, (_, i) => String(i + 1))
          : useAutoCombos
            ? Array.from(
                {
                  length: Math.min(
                    scenarioCount,
                    Math.pow(simLevels, nodes.length),
                  ),
                },
                (_, i) => String(i + 1),
              )
            : simScenarios
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

        const singleRunParams = {
          filename: "motif_model.xlsx",
          steps: isLocked ? 50 : Number(simSteps) || 50,
          runs: isLocked ? 10 : Number(simRuns) || 10,
          scenarios: scenarioList,
          scheme: isLocked ? "random" : simScheme,
          output_format: 1,
          normalize: false,
          difference: false,
          concatenate: false,
          randomize_each_run: false,
          round_trace: false,
          timed: false,
        };
        console.log(
          "[MOTIF SIM] run-script params (single):",
          JSON.stringify(singleRunParams, null, 2),
        );
        const rr = await fetch(`${API_BASE}/run-script`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(singleRunParams),
        });
        const txt = await rr.text();
        let runData;
        try {
          runData = JSON.parse(txt);
        } catch {
          throw new Error("Invalid server response");
        }
        if (!rr.ok)
          throw new Error(
            runData?.detail || runData?.error || `HTTP ${rr.status}`,
          );
        if (!runData?.success)
          throw new Error(runData?.error || "Simulation failed");

        console.log(
          "[MOTIF SIM] traces_data keys:",
          Object.keys(runData.traces_data?.data || {}),
        );
        const firstEl = Object.keys(runData.traces_data?.data || {})[0];
        if (firstEl) {
          const firstSc = Object.keys(runData.traces_data.data[firstEl])[0];
          console.log(
            "[MOTIF SIM] scenario keys for",
            firstEl,
            ":",
            Object.keys(runData.traces_data.data[firstEl]),
          );
          console.log(
            "[MOTIF SIM] value at [scenario0][0]:",
            JSON.stringify(runData.traces_data.data[firstEl][firstSc]),
          );
        }

        // Store raw data as-is, build plots from it directly
        const rawData = runData.traces_data?.data || {};
        const filteredData = {};
        for (const el of Object.keys(rawData)) {
          filteredData[el] = {};
          for (const sc of scenarioList) {
            if (rawData[el][sc] !== undefined)
              filteredData[el][sc] = rawData[el][sc];
          }
        }
        console.log(
          "[MOTIF SIM] filtered scenario keys:",
          scenarioList,
          "found:",
          Object.keys(Object.values(filteredData)[0] || {}),
        );

        setAllResults((prev) => ({
          ...prev,
          [runKey]: {
            label: preset?.label || runKey,
            color: modelColor,
            type: "single",
            data: rawData,
            traceFiles: runData.trace_files || [],
            paramSig: currentParamSig,
          },
        }));
        setPickedScenario(
          Object.keys(rawData).map((_, i) => `scenario${i + 1}`),
        );
      }
    } catch (err) {
      setSimError(err.message || "Simulation failed");
    } finally {
      setSimRunning(false);
    }
  }, [
    nodes,
    edges,
    nodeProps,
    isLocked,
    isGroup,
    groupKeys,
    preset,
    simSteps,
    simRuns,
    simScenarios,
    simScheme,
    simLevels,
    useAutoCombos,
    alreadyRan,
    currentRunKey,
    currentParamSig,
  ]);

  // ── LLM ──
  const fetchBio = async () => {
    if (nodes.length === 0) return;
    setBioLoading(true);
    setBioText(null);
    try {
      const res = await fetch(`${API_BASE}/biology-example/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nodes: nodes.map((n) => ({
            name: n.name,
            type: n.type,
            label: NODE_TYPES[n.type]?.label || n.type,
          })),
          edges: edges.map((e) => ({
            src_name: nodes.find((n) => n.id === e.src)?.name || "",
            tgt_name: nodes.find((n) => n.id === e.tgt)?.name || "",
            type: e.type,
          })),
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`Server error (${res.status}): ${err.slice(0, 200)}`);
      }
      const d = await res.json();
      setBioText(d.text || "No response.");
    } catch (err) {
      setBioText(`Could not reach /api/biology-example: ${err.message}`);
    } finally {
      setBioLoading(false);
    }
  };

  const runAll = () => {
    if (nodes.length === 0) return;
    runSimulation();
    fetchBio();
  };

  // ── Build plot rows from raw backend data ──
  // Backend format: { scenario0: { "0": [v1,v2,...], "1": [...], ... }, scenario1: ... }
  // OR:             { scenario0: { "0": number, "1": number, ... }, ... }
  // We need: one chart per scenario, one line per element (node)
  // Convert backend { scenario0: { "0": [{name,value}], "1": [...] } }
  // to Plots.jsx format { scenario0: [{name,value},{name,value},...] } (flat array)
  const toPlotFormat = (rawData) => {
    const out = {};
    Object.keys(rawData).forEach((scKey, idx) => {
      const displayKey = `scenario${idx + 1}`;
      const flat = [];
      Object.keys(rawData[scKey])
        .sort((a, b) => Number(a) - Number(b))
        .forEach((t) => {
          const items = rawData[scKey][t];
          (Array.isArray(items) ? items : [items]).forEach((item) => {
            if (item?.name !== undefined)
              flat.push({ name: item.name, value: item.value });
          });
        });
      out[displayKey] = flat;
    });
    return out;
  };

  // Same transform as Plots.jsx transformData
  const buildScenarioPlotRows = (flatItems) => {
    const byName = {};
    for (const { name, value } of flatItems) {
      (byName[name] ??= []).push(value);
    }
    const elementNames = Object.keys(byName);
    const steps = Math.min(...elementNames.map((n) => byName[n].length));
    return {
      rows: Array.from({ length: steps }, (_, i) => {
        const pt = { x: String(i + 1) };
        elementNames.forEach((name) => {
          pt[name] = byName[name][i];
        });
        return pt;
      }),
      elementNames,
    };
  };

  const PlotGrid = ({ rawData, pickedSc, onToggleSc, modelColor }) => {
    const plotData = toPlotFormat(rawData);
    const scKeys = Object.keys(plotData);
    const shown =
      pickedSc.length > 0 ? pickedSc.filter((s) => scKeys.includes(s)) : scKeys;
    const { elementNames } = buildScenarioPlotRows(plotData[scKeys[0]] || []);
    const colorMap = buildColorMap(elementNames, modelColor);
    return (
      <>
        {scKeys.length > 1 && (
          <div>
            <div className="motif-section-label" style={{ marginBottom: 5 }}>
              Show scenarios
            </div>
            <div className="motif-picker-row">
              {scKeys.map((sc) => (
                <button
                  key={sc}
                  className="motif-picker-pill"
                  onClick={() => onToggleSc(sc)}
                  style={{
                    background: pickedSc.includes(sc) ? "#FFB81C" : "#fff",
                    color: pickedSc.includes(sc) ? "#00205B" : "#888",
                    borderColor: pickedSc.includes(sc) ? "#FFB81C" : "#C8C9C7",
                  }}
                >
                  {sc}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="motif-plot-grid">
          {shown.map((sc) => {
            const { rows } = buildScenarioPlotRows(plotData[sc] || []);
            return (
              <div key={sc} className="motif-plot-card">
                <div className="motif-plot-card-title">{sc}</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart
                    data={rows}
                    margin={{ top: 8, right: 14, left: 0, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="x"
                      tick={{ fill: "#374151", fontSize: 10 }}
                      label={{
                        value: "Step",
                        position: "insideBottom",
                        offset: -10,
                        fontSize: 10,
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      label={{
                        value: "Level",
                        angle: -90,
                        position: "insideLeft",
                        fontSize: 10,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1e293b",
                        border: "none",
                        borderRadius: 8,
                        color: "white",
                        fontSize: 11,
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 6, fontSize: 10 }} />
                    {elementNames.map((name) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={colorMap[name] ?? "#2563EB"}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // ─────────────────────────────────────────────
  return (
    <div className="motif-tab-content">
      {/* ── Preset picker ── */}
      <div className="motif-panel">
        <div className="motif-panel-header">
          <span className="motif-panel-title">
            {presetLibrary === "computational"
              ? "⚙ Computational Presets"
              : "Load Preset"}
          </span>
          <span
            style={{
              fontSize: 10,
              color: "#888",
              fontFamily: "'DM Mono',monospace",
            }}
          >
            {/* {nodes.length > 0
              ? `current: ${nodes.length}N · ${edges.length}E`
              : "no motif loaded"} */}
          </span>
        </div>
        <div
          style={{
            padding: "12px 14px",
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: 16,
            alignItems: "start",
            height: '100%',
            
          }}
        >
          {/* ── Categorized picker ── */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 220,
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            {/* Biological section */}
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#003594",
                fontFamily: "'DM Mono',monospace",
                letterSpacing: "0.08em",
                padding: "2px 4px 4px",
                textTransform: "uppercase",
                borderBottom: "1px solid #e2e8f0",
                marginBottom: 2,
              }}
            >
              🧬 Biological
            </div>
            {[
              {
                id: "motif1",
                label: "Motif 1",
                color: "#003594",
                keys: Object.keys(PRESETS).filter((k) =>
                  k.startsWith("motif1"),
                ),
              },
              {
                id: "motif2",
                label: "Motif 2",
                color: "#036b45",
                keys: Object.keys(PRESETS).filter((k) =>
                  k.startsWith("motif2"),
                ),
              },
              {
                id: "motif3",
                label: "Motif 3",
                color: "#770538",
                keys: Object.keys(PRESETS).filter((k) =>
                  k.startsWith("motif3"),
                ),
              },
              {
                id: "motif4",
                label: "Motif 4",
                color: "#B85C00",
                keys: Object.keys(PRESETS).filter((k) =>
                  k.startsWith("motif4"),
                ),
              },
              {
                id: "motif5",
                label: "Motif 5",
                color: "#444",
                keys: Object.keys(PRESETS).filter((k) =>
                  k.startsWith("motif5"),
                ),
              },
            ].map((cat) => {
              const isOpen = openCategory === cat.id;
              return (
                <div key={cat.id}>
                  <button
                    onClick={() => setOpenCategory(isOpen ? null : cat.id)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      border: "1.5px solid",
                      borderRadius: 7,
                      cursor: "pointer",
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 12,
                      fontWeight: 700,
                      transition: "all 0.15s",
                      background: isOpen ? cat.color : "#fff",
                      color: isOpen ? "#fff" : cat.color,
                      borderColor: cat.color,
                    }}
                  >
                    <span>{cat.label}</span>
                    <span style={{ fontSize: 10, opacity: 0.7 }}>
                      {isOpen ? "▲" : "▼"} {cat.keys.length}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      style={{
                        marginTop: 3,
                        marginLeft: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {cat.keys.map((key) => {
                        const p = PRESETS[key];
                        const isSelected = previewKey === key;
                        const res = allResults[key];
                        const alreadyRanThis = !!res;
                        return (
                          <button
                            key={key}
                            onClick={() => {
                              setPreviewKey(key);
                              onLoadPreset(key);
                            }}
                            style={{
                              padding: "5px 10px",
                              fontSize: 11,
                              borderRadius: 6,
                              border: `1.5px solid ${alreadyRanThis ? res.color : isSelected ? cat.color : "#e2e8f0"}`,
                              textAlign: "left",
                              fontFamily: "'DM Mono',monospace",
                              cursor: "pointer",
                              background: alreadyRanThis
                                ? res.color + "22"
                                : isSelected
                                  ? cat.color + "12"
                                  : "#fafbfc",
                              color: alreadyRanThis
                                ? res.color
                                : isSelected
                                  ? cat.color
                                  : "#444",
                              fontWeight:
                                isSelected || alreadyRanThis ? "700" : "400",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              outline:
                                isSelected && alreadyRanThis
                                  ? `2px solid ${res.color}`
                                  : "none",
                              outlineOffset: "1px",
                            }}
                          >
                            {alreadyRanThis && (
                              <span
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  background: res.color,
                                  flexShrink: 0,
                                  display: "inline-block",
                                }}
                              />
                            )}
                            <span style={{ flex: 1 }}>{p.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Computational section */}
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#B85C00",
                fontFamily: "'DM Mono',monospace",
                letterSpacing: "0.08em",
                padding: "8px 4px 4px",
                textTransform: "uppercase",
                borderBottom: "1px solid #e2e8f0",
                marginBottom: 2,
                marginTop: 8,
              }}
            >
              ⚙ Computational
            </div>
            {Object.keys(COMP_PRESETS).length === 0 ? (
              <div
                style={{
                  fontSize: 10,
                  color: "#aaa",
                  fontFamily: "'DM Mono',monospace",
                  padding: "4px 4px",
                }}
              >
                No computational motifs yet
              </div>
            ) : (
              Object.entries(
                Object.keys(COMP_PRESETS).reduce((acc, key) => {
                  const p = COMP_PRESETS[key];
                  const cat = `Level ${p.levels}`;
                  (acc[cat] ??= []).push(key);
                  return acc;
                }, {}),
              )
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([catId, keys]) => {
                  const isOpen = openCategory === catId;
                  return (
                    <div key={catId}>
                      <button
                        onClick={() => setOpenCategory(isOpen ? null : catId)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          border: "1.5px solid #FFB81C",
                          borderRadius: 7,
                          cursor: "pointer",
                          fontFamily: "'DM Sans',sans-serif",
                          fontSize: 12,
                          fontWeight: 700,
                          background: isOpen ? "#FFB81C" : "#fff",
                          color: isOpen ? "#00205B" : "#B85C00",
                        }}
                      >
                        <span>⚙ {catId}</span>
                        <span style={{ fontSize: 10, opacity: 0.7 }}>
                          {isOpen ? "▲" : "▼"} {keys.length}
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          style={{
                            marginTop: 3,
                            marginLeft: 8,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {keys.map((key) => {
                            const p = COMP_PRESETS[key];
                            const isSelected = previewKey === key;
                            const res = allResults[key];
                            const alreadyRanThis = !!res;
                            return (
                              <button
                                key={key}
                                onClick={() => {
                                  setPreviewKey(key);
                                  onLoadPreset(key);
                                }}
                                style={{
                                  padding: "5px 10px",
                                  fontSize: 11,
                                  borderRadius: 6,
                                  border: `1.5px solid ${alreadyRanThis ? res.color : isSelected ? "#FFB81C" : "#e2e8f0"}`,
                                  textAlign: "left",
                                  fontFamily: "'DM Mono',monospace",
                                  cursor: "pointer",
                                  background: alreadyRanThis
                                    ? res.color + "22"
                                    : isSelected
                                      ? "#FFB81C12"
                                      : "#fafbfc",
                                  color: alreadyRanThis
                                    ? res.color
                                    : isSelected
                                      ? "#B85C00"
                                      : "#444",
                                  fontWeight:
                                    isSelected || alreadyRanThis
                                      ? "700"
                                      : "400",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                {alreadyRanThis && (
                                  <span
                                    style={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: "50%",
                                      background: res.color,
                                      flexShrink: 0,
                                      display: "inline-block",
                                    }}
                                  />
                                )}
                                <span style={{ flex: 1 }}>{p.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
            <span
              style={{
                fontSize: 10,
                color: "#aaa",
                fontFamily: "'DM Mono',monospace",
                marginTop: 4,
              }}
            >
              click category → pick model
            </span>
          </div>

          {/* ── Preview ── */}
          <div
            style={{
              background: "#fafbfc",
              border: "1.5px solid #e2e8f0",
              borderRadius: 8,
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              position: "relative",
              
            }}
          >
            {previewKey ? (
              <>
                <MotifPreview presetKey={previewKey} />
                <div
                  style={{
                    position: "absolute",
                    top: 6,
                    right: 8,
                    fontSize: 9,
                    color: "#aaa",
                    fontFamily: "'DM Mono',monospace",
                    background: "rgba(255,255,255,0.8)",
                    padding: "2px 6px",
                    borderRadius: 4,
                  }}
                >
                  read-only preview
                </div>
              </>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: "#C8C9C7",
                  fontFamily: "'DM Mono',monospace",
                  textAlign: "center",
                }}
              >
                pick a category
                <br />
                then a model
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="motif-sim-layout">
        <div className="motif-sim-left">
          {/* ── Params ── */}
          <div className="motif-panel">
            <div
              className="motif-panel-header"
              style={{ cursor: "pointer" }}
              onClick={() => setShowParams((p) => !p)}
            >
              <span className="motif-panel-title">Simulation Parameters</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isLocked ? (
                  <span
                    style={{
                      fontSize: 10,
                      background: "#FFB81C",
                      color: "#00205B",
                      padding: "2px 8px",
                      borderRadius: 10,
                      fontWeight: 700,
                      fontFamily: "'DM Mono',monospace",
                    }}
                  >
                    {` ${preset.scenarios} scenarios · level ${preset.levels}`}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 10,
                      color: "#888",
                      fontFamily: "'DM Mono',monospace",
                    }}
                  >
                    {/* → /api/run-script */}
                  </span>
                )}
                <span style={{ fontSize: 12, color: "#888" }}>
                  {showParams ? "▲" : "▼"}
                </span>
              </div>
            </div>
            {showParams && (
              <div
                style={{
                  padding: "12px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                {isLocked ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#555",
                        fontFamily: "'DM Mono',monospace",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 6,
                        padding: "8px 12px",
                        lineHeight: 1.7,
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#00205B" }}>
                        Fixed:{" "}
                      </span>
                      Steps 50 · Runs 10 · Scheme random · Level {preset.levels}
                      {isGroup
                        ? ` · ${groupKeys.length} models × ${preset.scenarios} scenarios each`
                        : ` · ${preset.scenarios} scenarios (SL1–SL${preset.scenarios})`}
                    </div>
                    <div
                      className="motif-section-label"
                      style={{ marginBottom: 4 }}
                    >
                      Spontaneous &amp; Balancing — set per node
                    </div>
                    {nodes.map((n) => {
                      const p = nodeProps[n.id] || DEFAULT_PROPS[n.type];
                      const t = NODE_TYPES[n.type];
                      const upd = (key, val) =>
                        setNodeProps((prev) => ({
                          ...prev,
                          [n.id]: { ...(prev[n.id] || {}), [key]: val },
                        }));
                      return (
                        <div
                          key={n.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "130px 1fr 1fr",
                            gap: 8,
                            alignItems: "center",
                            background: "#fafbfc",
                            border: "1px solid #e2e8f0",
                            borderRadius: 6,
                            padding: "8px 12px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: t.color,
                                flexShrink: 0,
                                display: "inline-block",
                              }}
                            />
                            <span
                              style={{
                                fontSize: 11,
                                fontFamily: "'DM Mono',monospace",
                                fontWeight: 700,
                                color: t.color,
                              }}
                            >
                              {n.name}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <span className="motif-prop-label">
                              Spontaneous
                            </span>
                            <select
                              className="motif-prop-select"
                              value={p.spontaneous || "none"}
                              onChange={(e) =>
                                upd("spontaneous", e.target.value)
                              }
                              disabled={simRunning}
                            >
                              {SPONT_BALANC_OPTIONS.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <span className="motif-prop-label">Balancing</span>
                            <select
                              className="motif-prop-select"
                              value={p.balancing || "none"}
                              onChange={(e) => upd("balancing", e.target.value)}
                              disabled={simRunning}
                            >
                              {SPONT_BALANC_OPTIONS.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                          display: "flex",
                          alignItems: 'center',
                        flexDirection:'column',
                        // gap: 14,
                        color: "#C8C9C7",
                        fontSize: "13px",
                      }}
                    >
                      {/* {[
                        {
                          label: "Steps",
                          val: simSteps,
                          set: setSimSteps,
                          w: 80,
                        },
                        { label: "Runs", val: simRuns, set: setSimRuns, w: 80 },
                      ].map((f) => (
                        <div
                          key={f.label}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <span className="motif-prop-label">{f.label}</span>
                          <input
                            className="motif-prop-input"
                            type="number"
                            style={{ width: f.w }}
                            value={f.val}
                            onChange={(e) => f.set(e.target.value)}
                            disabled={simRunning}
                          />
                        </div>
                      ))}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        <span className="motif-prop-label">Scheme</span>
                        <select
                          className="motif-prop-select"
                          style={{ width: 110 }}
                          value={simScheme}
                          onChange={(e) => setSimScheme(e.target.value)}
                          disabled={simRunning}
                        >
                          <option value="sync">sync</option>
                          <option value="ra">random</option>
                          <option value="round">round</option>
                        </select>
                      </div> */}
                      Select one of the motifs above
                    </div>
                    {/* <div
                      style={{
                        background: "#f8fafc",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: 8,
                        padding: "10px 14px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          className="motif-prop-label"
                          style={{ margin: 0 }}
                        >
                          Scenarios
                        </span>
                        <div
                          style={{
                            display: "flex",
                            border: "1.5px solid #C8C9C7",
                            borderRadius: 20,
                            overflow: "hidden",
                            fontSize: 11,
                            fontFamily: "'DM Mono',monospace",
                          }}
                        >
                          {[
                            { val: false, label: "Manual" },
                            { val: true, label: "Auto combinations" },
                          ].map((o) => (
                            <button
                              key={String(o.val)}
                              onClick={() => setUseAutoCombos(o.val)}
                              disabled={simRunning}
                              style={{
                                padding: "3px 12px",
                                border: "none",
                                cursor: "pointer",
                                fontFamily: "'DM Mono',monospace",
                                fontSize: 11,
                                background:
                                  useAutoCombos === o.val ? "#003594" : "#fff",
                                color:
                                  useAutoCombos === o.val ? "#fff" : "#888",
                                transition: "all 0.15s",
                              }}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {!useAutoCombos ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <span className="motif-tip">
                            Scenario IDs matching State List indices (e.g.
                            1,2,3)
                          </span>
                          <input
                            className="motif-prop-input"
                            type="text"
                            placeholder="1,2,3"
                            style={{ maxWidth: 200 }}
                            value={simScenarios}
                            onChange={(e) => setSimScenarios(e.target.value)}
                            disabled={simRunning}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <span className="motif-tip">
                            SL0=baseline. SL1+ = generated combos written into
                            Excel.
                          </span>
                          <div
                            style={{
                              display: "flex",
                              gap: 14,
                              flexWrap: "wrap",
                              alignItems: "flex-end",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                              }}
                            >
                              <span className="motif-prop-label">Level</span>
                              <input
                                className="motif-prop-input"
                                type="number"
                                min="2"
                                max="5"
                                style={{ width: 80 }}
                                value={simLevels}
                                onChange={(e) =>
                                  setSimLevels(
                                    Math.max(
                                      2,
                                      Math.min(
                                        5,
                                        parseInt(e.target.value) || 2,
                                      ),
                                    ),
                                  )
                                }
                                disabled={simRunning}
                              />
                              <span className="motif-tip">
                                values:{" "}
                                {Array.from(
                                  { length: simLevels },
                                  (_, i) => i,
                                ).join(", ")}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                              }}
                            >
                              <span className="motif-prop-label">
                                Number of scenarios
                              </span>
                              <input
                                className="motif-prop-input"
                                type="number"
                                min="1"
                                style={{ width: 110 }}
                                value={simScenarios}
                                onChange={(e) =>
                                  setSimScenarios(e.target.value)
                                }
                                disabled={simRunning}
                              />
                              {nodes.length > 0 &&
                                (() => {
                                  const max = Math.pow(simLevels, nodes.length),
                                    req = Number(simScenarios) || 0;
                                  return (
                                    <span
                                      className="motif-tip"
                                      style={{
                                        color: req > max ? "#FFB81C" : "#888",
                                      }}
                                    >
                                      {req > max
                                        ? `max ${max}`
                                        : `${Math.min(req, max)} of ${max} possible`}
                                    </span>
                                  );
                                })()}
                            </div>
                          </div>
                          {nodes.length > 0 &&
                            (() => {
                              const combos = generateCombinations(
                                nodes.length,
                                simLevels,
                              ).slice(0, Number(simScenarios) || 0);
                              if (!combos.length) return null;
                              const show = combos.slice(0, 10),
                                more = combos.length - show.length;
                              return (
                                <div style={{ overflowX: "auto" }}>
                                  <table
                                    style={{
                                      borderCollapse: "collapse",
                                      fontSize: 10,
                                      fontFamily: "'DM Mono',monospace",
                                      width: "100%",
                                    }}
                                  >
                                    <thead>
                                      <tr style={{ background: "#003594" }}>
                                        <th
                                          style={{
                                            padding: "4px 10px",
                                            color: "#fff",
                                            fontWeight: 700,
                                            textAlign: "left",
                                          }}
                                        >
                                          State List
                                        </th>
                                        {nodes.map((n) => (
                                          <th
                                            key={n.id}
                                            style={{
                                              padding: "4px 10px",
                                              color: NODE_TYPES[n.type].bg,
                                              fontWeight: 700,
                                              textAlign: "center",
                                            }}
                                          >
                                            {n.name}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr style={{ background: "#f1f5f9" }}>
                                        <td
                                          style={{
                                            padding: "3px 10px",
                                            color: "#888",
                                            fontWeight: 700,
                                          }}
                                        >
                                          SL 0
                                        </td>
                                        {nodes.map((n) => (
                                          <td
                                            key={n.id}
                                            style={{
                                              padding: "3px 10px",
                                              textAlign: "center",
                                              color: "#94a3b8",
                                            }}
                                          >
                                            0
                                          </td>
                                        ))}
                                      </tr>
                                      {show.map((combo, i) => (
                                        <tr
                                          key={i}
                                          style={{
                                            background:
                                              i % 2 === 0 ? "#fff" : "#f8fafc",
                                          }}
                                        >
                                          <td
                                            style={{
                                              padding: "3px 10px",
                                              color: "#003594",
                                              fontWeight: 700,
                                            }}
                                          >
                                            SL {i + 1}
                                          </td>
                                          {combo.split(",").map((v, vi) => (
                                            <td
                                              key={vi}
                                              style={{
                                                padding: "3px 10px",
                                                textAlign: "center",
                                                color: "#334155",
                                                fontWeight:
                                                  Number(v) > 0 ? "600" : "400",
                                              }}
                                            >
                                              {v}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {more > 0 && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "#aaa",
                                        padding: "4px 10px",
                                      }}
                                    >
                                      +{more} more…
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                        </div>
                      )}
                    </div> */}
                  </>
                )}
              </div>
            )}

            <div
              style={{
                padding: "0 16px 14px",
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                margin: "10px",
              }}
            >
              <button
                className="motif-btn motif-btn-primary"
                onClick={runSimulation}
                disabled={nodes.length === 0 || simRunning || alreadyRan}
                style={{
                  ...(nodes.length === 0 || simRunning || alreadyRan
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}),
                }}
              >
                {simRunning ? (
                  <>
                    <ReactLoading
                      type="spin"
                      color="#00205B"
                      width={14}
                      height={14}
                    />{" "}
                    {isGroup
                      ? `Running ${groupKeys.length} models…`
                      : "Running…"}
                  </>
                ) : alreadyRan ? (
                  "✓ Already ran — change params to re-run"
                ) : res ? (
                  "↺ Re-run (params changed)"
                ) : isGroup ? (
                  `▶ Simulate (${groupKeys.length} models × ${preset?.scenarios} scenarios)`
                ) : isLocked ? (
                  `▶ Simulate (${preset.scenarios} scenarios)`
                ) : (
                  `▶ Simulate${useAutoCombos && nodes.length > 0 ? ` (${Math.min(Number(simScenarios) || 0, Math.pow(simLevels, nodes.length))} scenarios)` : ""}`
                )}
              </button>

              {Object.keys(allResults).length > 0 && (
                <button
                  onClick={() => {
                    setAllResults({});
                    setActiveModel(null);
                    setPickedScenario([]);
                  }}
                  style={{
                    padding: "7px 14px",
                    fontSize: 11,
                    borderRadius: 8,
                    border: "1.5px solid #EF4444",
                    background: "#fff",
                    color: "#EF4444",
                    cursor: "pointer",
                    fontFamily: "'DM Mono',monospace",
                    fontWeight: 700,
                  }}
                >
                  ✕ Clear all results
                </button>
              )}
            </div>
          </div>

          {/* Error */}
          {simError && (
            <div
              style={{
                background: "#FFDECD",
                border: "1px solid #FF5B45",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 12,
                color: "#770538",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>⚠ {simError}</span>
              <button
                onClick={() => setSimError(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#FF5B45",
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Results */}
          <div className="motif-panel">
            <div className="motif-panel-header">
              <span className="motif-panel-title">Results</span>
              {Object.keys(allResults).length > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    color: "#00AD6E",
                    fontFamily: "'DM Mono',monospace",
                    fontWeight: 700,
                  }}
                >
                  ✓ {Object.keys(allResults).length} model
                  {Object.keys(allResults).length > 1 ? "s ran" : "ran"}
                </span>
              )}
            </div>
            <div
              style={{
                padding: "12px 14px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {/* Ran model tabs */}
              {Object.keys(allResults).length > 0 && (
                <div>
                  <div
                    className="motif-section-label"
                    style={{ marginBottom: 5 }}
                  >
                    Ran models — click to view
                  </div>
                  <div
                    className="motif-picker-row"
                    style={{ flexWrap: "wrap" }}
                  >
                    {Object.entries(allResults).map(([key, res]) => (
                      <button
                        key={key}
                        className="motif-picker-pill"
                        onClick={() => {
                          setActiveModel(key);
                          setSubActive(null);
                          const d =
                            res.type === "group"
                              ? res.models[Object.keys(res.models)[0]]?.data ||
                                {}
                              : res.data || {};
                          setPickedScenario(Object.keys(d));
                        }}
                        style={{
                          background: activeModel === key ? res.color : "#fff",
                          color: activeModel === key ? "#fff" : res.color,
                          borderColor: res.color,
                          fontWeight: activeModel === key ? "700" : "400",
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: res.color,
                            display: "inline-block",
                            marginRight: 5,
                            flexShrink: 0,
                          }}
                        />
                        {res.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Active model plots */}
              {activeModel &&
                allResults[activeModel] &&
                (() => {
                  const res = allResults[activeModel];
                  if (res.type === "group") {
                    const models = res.models;
                    const mKeys = Object.keys(models);
                    const curSub =
                      subActive && models[subActive] ? subActive : mKeys[0];
                    const curData = models[curSub]?.data || {};
                    return (
                      <>
                        <div>
                          <div
                            className="motif-section-label"
                            style={{ marginBottom: 5 }}
                          >
                            Sub-model
                          </div>
                          <div className="motif-picker-row">
                            {mKeys.map((k) => (
                              <button
                                key={k}
                                className="motif-picker-pill"
                                onClick={() => {
                                  setSubActive(k);
                                  setPickedScenario(
                                    Object.keys(models[k]?.data || {}),
                                  );
                                }}
                                style={{
                                  background: curSub === k ? res.color : "#fff",
                                  color: curSub === k ? "#fff" : res.color,
                                  borderColor: res.color,
                                  fontWeight: curSub === k ? "700" : "400",
                                }}
                              >
                                {models[k]?.label || k}
                              </button>
                            ))}
                          </div>
                        </div>
                        <PlotGrid
                          rawData={curData}
                          pickedSc={pickedScenario}
                          modelColor={res.color}
                          onToggleSc={(sc) =>
                            setPickedScenario((prev) =>
                              prev.includes(sc)
                                ? prev.filter((x) => x !== sc)
                                : [...prev, sc],
                            )
                          }
                        />
                      </>
                    );
                  }
                  return (
                    <PlotGrid
                      rawData={res.data || {}}
                      pickedSc={pickedScenario}
                      modelColor={res.color}
                      onToggleSc={(sc) =>
                        setPickedScenario((prev) =>
                          prev.includes(sc)
                            ? prev.filter((x) => x !== sc)
                            : [...prev, sc],
                        )
                      }
                    />
                  );
                })()}

              {simRunning && (
                <div className="motif-empty-state">
                  <ReactLoading
                    type="bubbles"
                    color="#003594"
                    width={40}
                    height={40}
                  />
                  <div style={{ color: "#003594", fontSize: 12 }}>
                    {isGroup
                      ? `Running ${groupKeys?.length} models…`
                      : "Running simulation…"}
                  </div>
                </div>
              )}
              {Object.keys(allResults).length === 0 && !simRunning && (
                <div className="motif-empty-state">
                  <div className="motif-empty-icon">📈</div>
                  <div>Load a preset and click Simulate</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right: bio + summary ── */}
        <div className="motif-sim-right">
          <div className="motif-panel" style={{ minHeight: 300 }}>
            <div className="motif-panel-header">
              <span className="motif-panel-title">Biological Example</span>
              <span
                style={{
                  fontSize: 10,
                  color: "#888",
                  fontFamily: "'DM Mono',monospace",
                }}
              >
                LLM-powered
              </span>
            </div>
            <div className="motif-bio-body">
              {bioLoading ? (
                <div className="motif-bio-card loading">
                  <div className="motif-typing">
                    <div className="motif-typing-dot" />
                    <div className="motif-typing-dot" />
                    <div className="motif-typing-dot" />
                    <span style={{ marginLeft: 6, fontSize: 12 }}>
                      Asking LLM…
                    </span>
                  </div>
                </div>
              ) : bioText ? (
                <div className="motif-bio-card">
                  <div className="motif-bio-label">Real Biological Example</div>
                  {loadedPresetKey && (
                    <div
                      style={{
                        fontSize: 10,
                        fontFamily: "'DM Mono',monospace",
                        color: "#888",
                        marginBottom: 6,
                      }}
                    >
                      for {PRESETS[loadedPresetKey]?.label || loadedPresetKey}
                    </div>
                  )}
                  {bioText}
                </div>
              ) : (
                <div className="motif-bio-card empty">
                  Click <strong>"▶ Simulate + Find Biology"</strong> to get a
                  real biological example from LLM.
                </div>
              )}
            </div>
            <button
              className="motif-btn motif-btn-green"
              onClick={fetchBio}
              disabled={nodes.length === 0 || bioLoading}
              style={{
                ...(nodes.length === 0 || bioLoading
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : {}),
                margin: '10px',
                alignItems: 'center',
                display:'flex',
                flexDirection:'column'
              }}
            >
              {bioLoading ? (
                <>
                  <ReactLoading
                    type="spin"
                    color="#fff"
                    width={14}
                    height={14}
                  />{" "}
                  Asking LLM…
                </>
              ) : (
                "Find Biology Example"
              )}
            </button>
          </div>

          {nodes.length > 0 && (
            <div className="motif-panel">
              <div className="motif-panel-header">
                <span className="motif-panel-title">Motif Summary</span>
              </div>
              <div
                style={{
                  padding: "10px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {nodes.map((n) => (
                  <div
                    key={n.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontSize: 11,
                      fontFamily: "'DM Mono',monospace",
                      color: "#333",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: NODE_TYPES[n.type].color,
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                    />
                    <span>{n.name}</span>
                    <span style={{ color: "#aaa", fontSize: 10 }}>
                      ({NODE_TYPES[n.type].label})
                    </span>
                  </div>
                ))}
                {edges.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                      borderTop: "1px solid #f0eeea",
                      paddingTop: 6,
                    }}
                  >
                    {edges.map((e, i) => {
                      const s = nodes.find((n) => n.id === e.src),
                        t = nodes.find((n) => n.id === e.tgt);
                      return s && t ? (
                        <div
                          key={i}
                          style={{
                            fontSize: 10,
                            fontFamily: "'DM Mono',monospace",
                            color: "#888",
                            marginBottom: 2,
                          }}
                        >
                          {s.name}{" "}
                          <span
                            style={{
                              color:
                                e.type === "activate" ? "#003594" : "#FF5B45",
                            }}
                          >
                            {e.type === "activate" ? "→" : "⊣"}
                          </span>{" "}
                          {t.name}
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MotifSimulate;
